import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchStorageStatus, migrateWeek } from "@/features/infrastructure/api";

// Monday-start of the most recent ENDED week (UTC): [lastMonday, thisMonday).
const lastEndedWeek = () => {
  const now = new Date();
  const day = (now.getUTCDay() + 6) % 7; // Mon=0
  const thisMonday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - day),
  );
  const from = new Date(thisMonday.getTime() - 7 * 86400000);
  return { from, to: thisMonday };
};

const statusColor = (s) =>
  s === "done"
    ? "text-green-600"
    : s === "failed"
      ? "text-red-600"
      : "text-amber-600";

export default function InfrastructurePage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setStatus(await fetchStorageStatus());
    } catch (e) {
      setError(String(e.message ?? e));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const { from, to } = useMemo(lastEndedWeek, []);
  const jobs = status?.jobs ?? [];
  const inFlight = jobs.some(
    (j) => j.status === "running" || j.status === "queued",
  );

  const onMigrate = async () => {
    setBusy(true);
    setError(null);
    try {
      await migrateWeek(from.toISOString(), to.toISOString());
      await load();
    } catch (e) {
      setError(String(e.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Infrastructure — Storage</h1>
        <p className="text-sm text-gray-500">
          Analytics reads migrated data from ClickHouse. Migrate a completed
          week from Postgres to ClickHouse to make it available.
        </p>
      </div>

      <div className="space-y-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm text-gray-500">Last migrated week</div>
            <div className="text-lg font-medium">
              {status?.lastMigratedWeek ?? "—"}
            </div>
          </div>
          <button
            type="button"
            onClick={onMigrate}
            disabled={busy || inFlight}
            className="rounded-md bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
          >
            {inFlight
              ? "Migrating…"
              : `Migrate last ended week (${from
                  .toISOString()
                  .slice(0, 10)} → ${to.toISOString().slice(0, 10)})`}
          </button>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>

      <div className="overflow-hidden rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="p-3">Week</th>
              <th className="p-3">Tier</th>
              <th className="p-3">Status</th>
              <th className="p-3">Counts</th>
              <th className="p-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr>
                <td colSpan={5} className="p-3 text-gray-400">
                  No migrations yet.
                </td>
              </tr>
            )}
            {jobs.map((j) => (
              <tr key={j.uuid} className="border-b">
                <td className="p-3">{j.week}</td>
                <td className="p-3">
                  {j.fromTier} → {j.toTier}
                </td>
                <td className={`p-3 font-medium ${statusColor(j.status)}`}>
                  {j.status}
                </td>
                <td className="p-3">
                  {j.counts
                    ? Object.entries(j.counts)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join("   ")
                    : (j.error ?? "—")}
                </td>
                <td className="p-3 text-gray-500">
                  {new Date(j.updatedAt).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
